/*
 * Copyright (C) 2022-2025 - Jeci SARL - https://jeci.fr
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import chatBackendService from "@/services/ChatBackendService";

/**
 * Authentication service for Rocket.Chat.
 * Handles both OAuth and personal token authentication modes.
 */
class RocketChatAuthService {
  constructor() {
    this.credentials = null;
    this.config = null;
  }

  /**
   * Initialize the auth service with configuration.
   *
   * @param {Object} config - Configuration object
   * @param {boolean} config.CHAT_ENABLED - Whether chat is enabled
   * @param {string} config.ROCKETCHAT_HOST - Rocket.Chat server URL
   * @param {string} config.ROCKETCHAT_WS_URL - Rocket.Chat WebSocket URL
   * @param {boolean} config.CHAT_OAUTH_ENABLED - Whether OAuth is enabled
   * @param {string} config.CHAT_OAUTH_SERVICE - OAuth service name (e.g., 'keycloak')
   */
  init(config) {
    this.config = config;
    this.credentials = null;
  }

  /**
   * Check if chat is enabled and configured.
   */
  isEnabled() {
    return this.config?.CHAT_ENABLED === true && !!this.config?.ROCKETCHAT_HOST;
  }

  /**
   * Check if OAuth authentication is enabled.
   */
  isOAuthEnabled() {
    return this.config?.CHAT_OAUTH_ENABLED === true;
  }

  /**
   * Get the Rocket.Chat server URL (without trailing slash).
   */
  getServerUrl() {
    const host = this.config?.ROCKETCHAT_HOST || "";
    // Remove trailing slash to prevent double slashes in URLs
    return host.replace(/\/$/, "");
  }

  /**
   * Get the Rocket.Chat WebSocket URL.
   */
  getWebSocketUrl() {
    if (this.config?.ROCKETCHAT_WS_URL) {
      return this.config.ROCKETCHAT_WS_URL;
    }

    // Derive WebSocket URL from HTTP URL
    const host = this.config?.ROCKETCHAT_HOST || "";
    const wsProtocol = host.startsWith("https") ? "wss" : "ws";
    // Remove protocol and trailing slash
    const wsHost = host.replace(/^https?:\/\//, "").replace(/\/$/, "");
    return `${wsProtocol}://${wsHost}/websocket`;
  }

  /**
   * Get authentication credentials.
   * Tries OAuth first if enabled, falls back to personal token.
   *
   * @returns {Promise<{userId: string, authToken: string}|null>}
   */
  async getCredentials() {
    if (this.credentials) {
      return this.credentials;
    }

    // Try to get credentials from Alfresco user profile
    const tokenData = await chatBackendService.getUserChatToken();

    if (tokenData.hasCredentials && tokenData.userId && tokenData.token) {
      this.credentials = {
        userId: tokenData.userId,
        authToken: tokenData.token,
      };
      return this.credentials;
    }

    console.warn(
      "[RocketChatAuthService] No credentials available. User needs to configure token in profile.",
    );
    return null;
  }

  /**
   * Clear cached credentials.
   */
  clearCredentials() {
    this.credentials = null;
  }

  /**
   * Verify that credentials are valid by making a test API call.
   *
   * @returns {Promise<boolean>}
   */
  async verifyCredentials() {
    const creds = await this.getCredentials();
    if (!creds) {
      return false;
    }

    try {
      const response = await fetch(`${this.getServerUrl()}/api/v1/me`, {
        method: "GET",
        headers: {
          "X-Auth-Token": creds.authToken,
          "X-User-Id": creds.userId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.success === true;
      }

      // Credentials invalid, clear them
      this.clearCredentials();
      return false;
    } catch (error) {
      console.error(
        "[RocketChatAuthService] Error verifying credentials:",
        error,
      );
      return false;
    }
  }

  /**
   * Get the current user's Rocket.Chat profile.
   *
   * @returns {Promise<Object|null>}
   */
  async getCurrentUser() {
    const creds = await this.getCredentials();
    if (!creds) {
      return null;
    }

    try {
      const response = await fetch(`${this.getServerUrl()}/api/v1/me`, {
        method: "GET",
        headers: {
          "X-Auth-Token": creds.authToken,
          "X-User-Id": creds.userId,
        },
      });

      if (response.ok) {
        return await response.json();
      }

      return null;
    } catch (error) {
      console.error(
        "[RocketChatAuthService] Error getting current user:",
        error,
      );
      return null;
    }
  }
}

export default new RocketChatAuthService();
